/**
 * Centralized Port Configuration
 *
 * This file is the single source of truth for all port-related configuration.
 * When adding a new port, add it to the PORTS object below.
 *
 * @see README.md for instructions on adding a new port
 */

// ============= TYPE DEFINITIONS =============

/**
 * Port location coordinates (used for geolocation detection)
 * Uses lat/lon format matching the geolocation API
 * Using readonly to support `as const` in PORTS definition
 */
export type TPortLocation = {
  readonly lat: number
  readonly lon: number
}

/**
 * Map center coordinates for focusing the map on a port
 * Uses latitude/longitude format matching react-map-gl
 * Using readonly to support `as const` in PORTS definition
 */
export type TMapCenter = {
  readonly latitude: number
  readonly longitude: number
}

/**
 * GeoJSON polygon coordinates for port boundary display
 * Format: [longitude, latitude][] (GeoJSON standard)
 * Using readonly to support `as const` in PORTS definition
 */
export type TPolygonCoordinates = readonly (readonly [number, number])[]

/**
 * Complete port configuration
 */
export type TPortConfig = {
  /** Unique identifier matching LOCALIZE_VERSION key (e.g., 'SANTOS', 'HOUSTON') */
  key: string

  /** UN/LOCODE for the port (e.g., 'BRSSZ', 'USHOU') */
  unlocode: string

  /** ISO 3166-1 alpha-2 country code (e.g., 'BR', 'US') */
  countryCode: string

  /** Port location for geolocation proximity detection */
  location: TPortLocation

  /** Map center point for focusing the map on this port */
  mapCenter: TMapCenter

  /** Polygon coordinates for rendering port boundary on map */
  polygonCoordinates: TPolygonCoordinates

  /** URL slug for routing (e.g., 'santos', 'houston') */
  slug: string

  /** Path to country/port logo (relative to public directory) */
  logoPath: string

  /** Domain suffix that locks to this port (e.g., '.com.br') - optional */
  domainLock?: string

  /** Default locale for this port's region (e.g., 'pt', 'en') */
  defaultLocale: 'en' | 'pt'

  /** Translation key for port name (used in i18n) */
  translationKey: string

  /** Use this port as a near port for geolocation detection */
  useAsNearPort: boolean
}

/**
 * Global configuration (no specific port)
 */
export type TGlobalConfig = {
  key: 'GLOBAL'
  unlocode: null
  logoPath: string
  defaultLocale: 'en'
  translationKey: 'GLOBAL'
}

// TLocalizeVersion is defined after PORTS to allow type derivation

/**
 * Port display configuration for backward compatibility
 */
export type TPortDisplayConfig = {
  portNameKey: string
  portCode: string | null
  logoPath: string
}

// type of the all port slugs
export type TPortSlug = (typeof PORTS)[keyof typeof PORTS]['slug']

// ============= CONSTANTS =============

/**
 * Base path for assets (from environment or empty)
 */
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH ?? ''

/**
 * Proximity detection distance in kilometers
 */
export const NEAR_PORT_DISTANCE_KM = 500

// ============= PORT CONFIGURATIONS =============

/**
 * Complete ports configuration registry
 * Add new ports here to enable them throughout the application.
 *
 * Using `satisfies` instead of explicit type annotation preserves the literal
 * keys ('SANTOS', 'HOUSTON') which allows TLocalizeVersion to be derived
 * automatically - no need to manually update a separate type definition!
 */
export const PORTS = {
  SANTOS: {
    key: 'SANTOS',
    unlocode: 'BRSSZ',
    countryCode: 'BR',
    location: { lat: -23.976, lon: -46.2889 },
    mapCenter: { latitude: -23.936559924410947, longitude: -46.32830265556127 },
    polygonCoordinates: [
      [-46.40577241353948, -23.858455123433245],
      [-46.41955877611792, -23.863782535717995],
      [-46.39827276537573, -23.921333858891696],
      [-46.355217551943284, -23.96460188494691],
      [-46.30807219577587, -23.990230194932643],
      [-46.30169716038861, -24.00629459762285],
      [-46.28002360469512, -24.01276477333371],
      [-46.26518174581487, -23.940536148396315],
      [-46.338481153773856, -23.89298886327658],
      [-46.37109681539495, -23.845895116325003],
      [-46.40577241353948, -23.858455123433245]
    ],
    slug: 'santos',
    logoPath: `${BASE_PATH}/country/br.svg`,
    domainLock: undefined,
    defaultLocale: 'pt',
    translationKey: 'SANTOS',
    useAsNearPort: true
  },
  HOUSTON: {
    key: 'HOUSTON',
    unlocode: 'USHOU',
    countryCode: 'US',
    location: { lat: 29.75888387431035, lon: -95.18301773873915 },
    mapCenter: { latitude: 29.75888387431035, longitude: -95.18301773873915 },
    polygonCoordinates: [
      [-95.18449238682706, 29.76062872837666],
      [-95.18442004875452, 29.761013488166586],
      [-95.18282958037956, 29.7606189735823],
      [-95.18293166746386, 29.76033835900994],
      [-95.18153429031376, 29.758970805341335],
      [-95.18280565738684, 29.757857784635135],
      [-95.18289685249333, 29.757303598167407],
      [-95.18311679363255, 29.757126630414504],
      [-95.18372833728796, 29.75768081786085],
      [-95.1828163862229, 29.758537706703166],
      [-95.18411457538609, 29.759655376790608],
      [-95.18449238682706, 29.76062872837666]
    ],
    slug: 'houston',
    logoPath: `${BASE_PATH}/country/us.svg`,
    domainLock: undefined,
    defaultLocale: 'en',
    translationKey: 'HOUSTON',
    useAsNearPort: true
  },
  SINGAPORE: {
    key: 'SINGAPORE',
    unlocode: 'SGSIN',
    countryCode: 'SG',
    location: { lat: 1.2677032655692158, lon: 103.80983209440416 },
    mapCenter: { latitude: 1.2677032655692158, longitude: 103.80983209440416 },
    polygonCoordinates: [
      [103.63339033876012, 1.353186661574859],
      [103.60624492560865, 1.2789367029229155],
      [103.60508100816274, 1.2196208818161889],
      [103.66740718516385, 1.2024854904475935],
      [103.66021156311037, 1.1970578895738357],
      [103.67415904998781, 1.1929818186103072],
      [103.73844623565675, 1.1455487857775564],
      [103.77455388388935, 1.171850512276997],
      [103.81110191345216, 1.1888413824493214],
      [103.88165930099356, 1.2200768496840593],
      [103.9544225144462, 1.260150177857387],
      [103.97435188293458, 1.2609226812703014],
      [104.04501021555855, 1.2817744041100199],
      [104.10099966098431, 1.2968034175451528],
      [104.06391868804913, 1.337296756084233],
      [104.03489552668734, 1.3621658074688823],
      [104.01411358729989, 1.3732006510706352],
      [104.00170647231981, 1.3945473563210955],
      [103.97483230583623, 1.3953676242700268],
      [103.95026640695112, 1.3846519295915403],
      [103.91383755107543, 1.4232851403756512],
      [103.90024844351073, 1.422015238167753],
      [103.87928541191499, 1.4354238623427533],
      [103.83916682076878, 1.468542057935975],
      [103.82424679240701, 1.4754028402790835],
      [103.80067237594231, 1.4719638612691184],
      [103.75764341605978, 1.442775897846076],
      [103.7414516767146, 1.4420595659833808],
      [103.71833791237977, 1.4553855566497844],
      [103.68086393557589, 1.436238875729241],
      [103.66501929226811, 1.4131459354559335],
      [103.6579931547568, 1.3931252697316272],
      [103.63339033876012, 1.353186661574859]
    ],
    slug: 'singapore',
    logoPath: `${BASE_PATH}/country/sg.svg`,
    domainLock: undefined,
    defaultLocale: 'en',
    translationKey: 'SINGAPORE',
    useAsNearPort: true
  },
  PARANAGUA: {
    key: 'PARANAGUA',
    unlocode: 'BRPNG',
    countryCode: 'BR',
    location: { lat: -25.505072544502163, lon: -48.53199014486265 },
    mapCenter: { latitude: -25.505072544502163, longitude: -48.53199014486265 },
    polygonCoordinates: [
      [-48.545989086219556, -25.517505467992958],
      [-48.537943126914406, -25.511495856720607],
      [-48.52525243581724, -25.505318095250566],
      [-48.48999661187322, -25.51298699290331],
      [-48.48903188528013, -25.492612985195528],
      [-48.554778192653174, -25.496176749213483],
      [-48.560271356715674, -25.5046983649612],
      [-48.545989086219556, -25.517505467992958]
    ],
    slug: 'paranagua',
    logoPath: `${BASE_PATH}/country/br.svg`,
    domainLock: undefined,
    defaultLocale: 'pt',
    translationKey: 'PARANAGUA',
    useAsNearPort: false
  },
  SALVADOR: {
    key: 'SALVADOR',
    unlocode: 'BRSSA',
    countryCode: 'BR',
    location: { lat: -12.97682204904494, lon: -38.521980285644545 },
    mapCenter: { latitude: -12.97682204904494, longitude: -38.521980285644545 },
    polygonCoordinates: [
      [-38.49277240331509, -13.018439823469565],
      [-38.4760093688965, -12.9376452846243],
      [-38.49574173415467, -12.904097811460376],
      [-38.52482855551369, -12.925944966542403],
      [-38.508296089300465, -12.948455795490522],
      [-38.54247644118451, -13.013255041852945],
      [-38.49277240331509, -13.018439823469565]
    ],
    slug: 'salvador',
    logoPath: `${BASE_PATH}/country/br.svg`,
    domainLock: undefined,
    defaultLocale: 'pt',
    translationKey: 'SALVADOR',
    useAsNearPort: false
  },
  SUAPE: {
    key: 'SUAPE',
    unlocode: 'BRSUA',
    countryCode: 'BR',
    location: { lat: -8.37369495636074, lon: -34.96749114990234 },
    mapCenter: { latitude: -8.37369495636074, longitude: -34.96749114990234 },
    polygonCoordinates: [
      [-34.9793529510498, -8.35307777561849],
      [-34.9938583374023, -8.40122453312176],
      [-34.9481964111328, -8.41022485236884],
      [-34.936695098877, -8.35086984507612],
      [-34.9793529510498, -8.35307777561849]
    ],
    slug: 'suape',
    logoPath: `${BASE_PATH}/country/br.svg`,
    domainLock: undefined,
    defaultLocale: 'pt',
    translationKey: 'SUAPE',
    useAsNearPort: false
  },
  FORTALEZA: {
    key: 'FORTALEZA',
    unlocode: 'BRFOR',
    countryCode: 'BR',
    location: { lat: -3.718644515923026, lon: -38.5063247680664 },
    mapCenter: { latitude: -3.718644515923026, longitude: -38.5063247680664 },
    polygonCoordinates: [
      [-38.5414981842041, -3.73287961853643],
      [-38.459358215332, -3.73287961853643],
      [-38.467106034908184, -3.709360027779206],
      [-38.47330978524329, -3.7015089595494755],
      [-38.4816524510067, -3.698838744024113],
      [-38.48668406468663, -3.712266980797483],
      [-38.530788895230685, -3.7083661420807594],
      [-38.5414981842041, -3.73287961853643]
    ],
    slug: 'fortaleza',
    logoPath: `${BASE_PATH}/country/br.svg`,
    domainLock: undefined,
    defaultLocale: 'pt',
    translationKey: 'FORTALEZA',
    useAsNearPort: false
  },
  PECEM: {
    key: 'PECEM',
    unlocode: 'BRPEC',
    countryCode: 'BR',
    location: { lat: -3.535237798067577, lon: -38.80888938903809 },
    mapCenter: { latitude: -3.535237798067577, longitude: -38.80888938903809 },
    polygonCoordinates: [
      [-38.822078704834, -3.54751682888408],
      [-38.8140106201172, -3.55317078222904],
      [-38.7877464294434, -3.53132577132677],
      [-38.7997627258301, -3.51581978483808],
      [-38.8076591491699, -3.51607679224341],
      [-38.822078704834, -3.54751682888408]
    ],
    slug: 'pecem',
    logoPath: `${BASE_PATH}/country/br.svg`,
    domainLock: undefined,
    defaultLocale: 'pt',
    translationKey: 'PECEM',
    useAsNearPort: false
  }
} as const satisfies Record<string, TPortConfig>

/**
 * Port key type derived from PORTS object keys
 * This updates automatically when you add/remove ports from PORTS!
 */
export type TPortKey = keyof typeof PORTS

/**
 * Localize version type - either GLOBAL or a port key
 * Derived from PORTS keys, so adding a new port automatically includes it here
 */
export type TLocalizeVersion = 'GLOBAL' | TPortKey

/**
 * Global (non-port-specific) configuration
 */
export const GLOBAL_CONFIG: TGlobalConfig = {
  key: 'GLOBAL',
  unlocode: null,
  logoPath: `${BASE_PATH}/logo.svg`,
  defaultLocale: 'en',
  translationKey: 'GLOBAL'
}

/**
 * LOCALIZE_VERSION enum for backward compatibility
 * Dynamically generated from PORTS keys plus GLOBAL.
 * Adding a new port to PORTS automatically includes it here!
 */
export const LOCALIZE_VERSION = {
  GLOBAL: 'GLOBAL',
  ...Object.fromEntries(Object.keys(PORTS).map((k) => [k, k]))
} as { [K in TLocalizeVersion]: K }

/**
 * Supported port slugs derived from PORTS configuration
 * This updates automatically when you add/remove ports!
 */
export const SUPPORTED_PORTS = Object.values(PORTS).map((p) => p.slug)

/**
 * Type for supported port slugs - derived from PORTS slugs
 * Note: We use a manual tuple type here because Object.values().map() loses literal types.
 * When adding a new port, add its slug to the tuple type above.
 */
export type TSupportedPort = (typeof SUPPORTED_PORTS)[number]

// ============= HELPER FUNCTIONS =============

/**
 * Get port configuration by localize version key
 * @param localizeVersion - The localize version key (e.g., 'SANTOS', 'HOUSTON', 'GLOBAL')
 * @returns The port configuration or null if GLOBAL or not found
 */
export function getPortConfig(localizeVersion: TLocalizeVersion): TPortConfig | null {
  if (localizeVersion === 'GLOBAL') return null
  return PORTS[localizeVersion]
}

/**
 * Get port configuration by UN/LOCODE
 * @param unlocode - The UN/LOCODE (e.g., 'BRSSZ', 'USHOU')
 * @returns The port configuration or null if not found
 */
export function getPortByUnlocode(unlocode: string): TPortConfig | null {
  return Object.values(PORTS).find((p) => p.unlocode === unlocode) ?? null
}

/**
 * Get port configuration by URL slug
 * @param slug - The URL slug (e.g., 'santos', 'houston')
 * @returns The port configuration or null if not found
 */
export function getPortBySlug(slug: string): TPortConfig | null {
  return Object.values(PORTS).find((p) => p.slug === slug) ?? null
}

/**
 * Get localize version from port slug
 * @param slug - The URL slug (e.g., 'santos', 'houston')
 * @returns The localize version key or 'GLOBAL' if not found
 */
export function getLocalizeVersionFromSlug(slug: string): TLocalizeVersion {
  const port = getPortBySlug(slug)
  return port ? (port.key as TLocalizeVersion) : 'GLOBAL'
}

/**
 * Get UN/LOCODE for a localize version
 * @param localizeVersion - The localize version key
 * @returns The UN/LOCODE or null for GLOBAL
 */
export function getUnlocodeForVersion(localizeVersion: TLocalizeVersion): string | null {
  const port = getPortConfig(localizeVersion)
  return port?.unlocode ?? null
}

/**
 * Get the default locale for a localize version
 * @param localizeVersion - The localize version key (e.g., 'SANTOS', 'HOUSTON', 'GLOBAL')
 * @returns The default locale for the port or GLOBAL's default locale
 */
export function getDefaultLocaleForVersion(localizeVersion: TLocalizeVersion): string {
  if (localizeVersion === 'GLOBAL') {
    return GLOBAL_CONFIG.defaultLocale
  }
  const port = getPortConfig(localizeVersion)
  return port?.defaultLocale ?? GLOBAL_CONFIG.defaultLocale
}

/**
 * Get default port (first port in registry)
 * @returns The default port configuration (currently Santos)
 */
export function getDefaultPort(): TPortConfig {
  return PORTS.SANTOS
}

/**
 * Get all port keys as array
 * @returns Array of port keys (e.g., ['SANTOS', 'HOUSTON'])
 */
export function getAllPortKeys(): string[] {
  return Object.keys(PORTS)
}

/**
 * Check if a hostname matches any port's domain lock
 * @param hostname - The hostname to check (e.g., 'portcallone.com.br')
 * @returns The port configuration if domain-locked, null otherwise
 */
export function getPortByDomainLock(hostname: string): TPortConfig | null {
  return Object.values(PORTS).find((p) => p.domainLock && hostname.endsWith(p.domainLock)) ?? null
}

/**
 * Check if localization selector should be disabled based on the current domain
 * @param hostname - The hostname to check (e.g., 'portcallone.com.br')
 * @returns Object with disabled state and the locked port (if any)
 */
export function getLocalizationLockInfo(hostname: string): {
  isLocked: boolean
  lockedPort: TPortConfig | null
} {
  const lockedPort = getPortByDomainLock(hostname)
  return {
    isLocked: lockedPort !== null,
    lockedPort
  }
}

/**
 * Get display configuration for a localize version (backward compatible)
 * @param localizeVersion - The localize version key
 * @returns Display configuration with portNameKey, portCode, and logoPath
 */
export function getPortDisplayConfig(localizeVersion: TLocalizeVersion): TPortDisplayConfig {
  if (localizeVersion === 'GLOBAL') {
    return {
      portNameKey: GLOBAL_CONFIG.translationKey,
      portCode: GLOBAL_CONFIG.unlocode,
      logoPath: GLOBAL_CONFIG.logoPath
    }
  }
  const port = getPortConfig(localizeVersion)
  if (!port) {
    return {
      portNameKey: GLOBAL_CONFIG.translationKey,
      portCode: null,
      logoPath: GLOBAL_CONFIG.logoPath
    }
  }
  return {
    portNameKey: port.translationKey,
    portCode: port.unlocode,
    logoPath: port.logoPath
  }
}
